import { useContext, useMemo } from "react";
import { FILTER_NAMES } from "@/constants/tasks";
import MainContext from "@/layouts/MainLayout/context";

/**
 * Хук useSelectedTaskFilters
 *
 * Назначение:
 * Преобразует активные фильтры из состояния tableParams в массив объектов для отображения
 * в виде тегов/плашек в UI. Каждый фильтр форматируется с читаемым названием и уникальным ключом.
 *
 * Что делает:
 * 1. Извлекает из tableParams.filters все применённые фильтры
 * 2. Для каждого фильтра ищет соответствующий объект в справочниках (statuses, priorities, employees)
 * 3. Форматирует найденные фильтры для отображения с префиксом (например "Статус: Открыта")
 * 4. Генерирует уникальный ключ для каждого фильтра в формате "FILTER_NAME__value"
 * 5. Возвращает отсортированный массив только применённых фильтров
 *
 * Используется в:
 * - Отображении активных фильтров под поиском на странице задач
 *
 * @param {Object} tableParams - объект параметров таблицы (filters, pagination, sort и т.д.)
 * @param {Array} statuses - список доступных статусов для поиска
 * @param {Array} priorities - список доступных приоритетов для поиска
 * @param {Array} employees - список доступных сотрудников для поиска
 *
 * @returns {Array} Массив отформатированных фильтров для отображения
 *
 * Пример вывода:
 * [
 *   { uuid: 'status-123', label: 'Статус: Открыта', key: 'status_uuid__status-123' },
 *   { uuid: 'user-456', label: 'Автор: Иванов И.И.', key: 'author_uuid__user-456' }
 * ]
 */
export const useSelectedTaskFilters = ({
  tableParams,
  statuses,
  priorities,
  taskTypes,
  employees,
  sprints,
}) => {
  const { config } = useContext(MainContext);
  const sprintName = config?.entities?.sprint?.name?.singular || "Спринт";
  return useMemo(() => {
    const {
      status_uuid,
      importance_uuid,
      task_type_uuid,
      author_uuid,
      responsible_executor_uuid,
      copy_transaction_id,
      sprint_uuid,
    } = tableParams.filters;

    // Преобразуем массив UUID статусов в объекты статусов
    const statusesList = (status_uuid || [])
      .map((id) => statuses.find(({ uuid }) => uuid === id))
      .filter(Boolean);

    // Ищем объекты приоритета, автора и исполнителя по их UUID
    const importance = priorities.find(({ uuid }) => uuid === importance_uuid);
    const taskType = taskTypes.find(({ uuid }) => uuid === task_type_uuid);
    const author = employees.find(({ uuid }) => uuid === author_uuid);
    const responsibleExecutor =
      responsible_executor_uuid === "notFilled"
        ? {
            uuid: "notFilled",
            name: "Не заполнено",
            label: "Не заполнено",
          }
        : employees.find(({ uuid }) => uuid === responsible_executor_uuid);
    const sprint =
      sprint_uuid === "notFilled"
        ? {
            uuid: "notFilled",
            name: `${sprintName}: Не заполнено`,
            label: `${sprintName}: Не заполнено`,
          }
        : sprints.find(({ uuid }) => uuid === sprint_uuid);

    // Формируем массив отформатированных фильтров для отображения
    return [
      // Статусы (может быть несколько)
      ...statusesList.map((s) => ({
        ...s,
        label: `Статус: ${s.label}`,
        value: `${FILTER_NAMES.STATUS_UUID}__${s.uuid}`,
      })),
      // Приоритет (один или отсутствует)
      importance && {
        ...importance,
        label: `Приоритет: ${importance.label}`,
        value: `${FILTER_NAMES.IMPORTANCE_UUID}__${importance.uuid}`,
      },
      taskType && {
        ...taskType,
        label: `Тип задачи: ${taskType.label}`,
        value: `task_type_uuid__${taskType.uuid}`,
      },
      // Автор (один или отсутствует)
      author && {
        ...author,
        label: `Автор: ${author.label}`,
        value: `${FILTER_NAMES.AUTHOR_UUID}__${author.uuid}`,
      },
      // Исполнитель (один или отсутствует)
      responsibleExecutor && {
        ...responsibleExecutor,
        label: `Исполнитель: ${responsibleExecutor.label}`,
        value: `${FILTER_NAMES.RESPONSIBLE_EXECUTOR_UUID}__${responsibleExecutor.uuid}`,
      },
      // Фильтр скопированных задач (специальный, выстанавливается по параметру URL)
      copy_transaction_id && {
        label: "Скопированные задачи",
        value: `${FILTER_NAMES.COPY_TRANSACTION_ID}__${copy_transaction_id}`,
        uuid: copy_transaction_id,
      },
      // Спринт
      sprint && {
        ...sprint,
        label: sprint.name,
        value: `${FILTER_NAMES.SPRINT_UUID}__${sprint.uuid}`,
      },
    ].filter(Boolean); // Убираем false и undefined элементы
  }, [tableParams, statuses, priorities, employees, taskTypes, sprints, sprintName]);
};
